/**
 * @fileoverview Future Protea Admin Login
 * @module Auth
 *
 * @description
 * Simple email + password login for cricket admin.
 * Split-screen layout: form on left, brand panel with the cricket
 * illustration (`/images/login.png`) on right. On mobile the same
 * elements stack vertically — logo + form on top, illustration anchored
 * at the bottom — matching the supplied reference mock.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '@/components/theme-toggle';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';

// Max field lengths chosen to be well above any reasonable input
// (320 is the RFC-5321 hard cap for an email address; 128 is comfortably
// longer than any bcrypt-compatible password — bcrypt itself truncates
// at 72 bytes). The point isn't business-rule enforcement, it's closing
// the trivial DoS surface where a malicious client posts a multi-MB
// "password" string and forces the backend to allocate / hash it.
const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .max(320, 'Email is too long')
    .email('Enter a valid email address'),
  password: z
    .string()
    .trim()
    .min(1, 'Password is required')
    .max(128, 'Password is too long'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });
  const watched = watch();
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = handleSubmit(async (values) => {
    const result = await login({ email: values.email, password: values.password });
    if (result.success) {
      toast.success('Login successful!');
      // Navigation is handled by the isAuthenticated useEffect
    } else {
      toast.error(result.message || 'Invalid credentials');
    }
  });

  const submitDisabled =
    isSubmitting || !watched.email?.trim() || !watched.password?.trim();

  if (isAuthenticated) return null;

  return (
    <div className="flex h-screen overflow-hidden">
      {/* ─── Left side ─ Form column ───────────────────────────────────
          Desktop (`lg:`) takes 50% width and centres the form vertically
          on a clean white surface. Mobile takes full width with the
          stacked layout that mirrors the supplied reference mock.
          `relative` anchors the decorative `login-side.png` brush-stroke
          accent we pin to the bottom-left below — same green/yellow
          motif as the brand panel on the right so the two halves share
          a visual language. */}
      <div className="flex-1 relative flex flex-col justify-start lg:justify-center px-6 pt-16 pb-0 sm:pt-20 lg:px-16 lg:py-8 overflow-y-auto bg-background">
        <div className="absolute top-4 left-4 lg:top-6 lg:left-6 z-50">
          <ThemeToggle />
        </div>

        {/* Decorative bottom-left flourish (desktop only). Sized large
            enough to read as a real brand accent — the reference mock
            shows the brush stroke sweeping across roughly the bottom
            third of the form column. Steps up at `xl:` for ultrawide
            screens so the proportion holds. Pinned with `absolute
            bottom-0 left-0` so it hugs the corner without affecting the
            form's vertical centring. `pointer-events-none` keeps it
            inert; `select-none` prevents accidental drag. Mobile
            already carries its own bottom illustration further down the
            column, so we hide this one below `lg:` to avoid stacking
            two illustrations on phones. */}
        <img
          src="/images/login-side.png"
          alt=""
          aria-hidden="true"
          className="hidden lg:block absolute bottom-0 left-0 w-[95%] xl:w-[90%] max-w-[960px] h-auto pointer-events-none select-none z-0"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          // `relative z-10` so the form card stays above the bottom-left
          // brush-stroke artwork on desktop. Mobile is unaffected — the
          // artwork is hidden there.
          className="relative z-10 w-full max-w-md mx-auto"
        >
          {/* Mobile-only brand logo + wordmark — the reference shows the
              "FUTURE PROTEA / LOG EVERY BALL" badge dominating the top
              of the viewport above the Admin Login form. Sized large
              on phones (`h-48 sm:h-56`) to match the reference mock;
              hidden on `lg:` since the right-hand panel handles the
              brand on desktop. */}
          <motion.div
            className="mb-4 sm:mb-6 lg:hidden text-center flex flex-col items-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <img
              src="/images/web_logo.png"
              alt="Future Protea"
              className="block h-48 sm:h-56 w-auto pointer-events-none"
            />
          </motion.div>

          {/* Form card. On desktop the white surface + heavy shadow
              gives the card its lifted look. On mobile the reference
              shows the form sitting directly on the page background —
              no card border, no white fill, no shadow — so we strip
              all three below `lg:`. `lg:` brings them back so the
              desktop split-screen still reads as a floating card. */}
          <Card className="border-0 bg-transparent shadow-none lg:bg-card lg:shadow-2xl lg:shadow-primary/10 overflow-visible lg:overflow-hidden">
            <CardHeader className="text-center pb-4">
              <CardTitle className="flex items-center justify-center gap-2 text-xl">
                <Shield className="h-5 w-5" />
                Admin Login
              </CardTitle>
              <CardDescription>Log every ball</CardDescription>
            </CardHeader>

            <CardContent>
              <motion.form
                onSubmit={onSubmit}
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@cricket.com"
                      className="pl-10"
                      autoFocus
                      aria-invalid={errors.email ? 'true' : 'false'}
                      {...register('email')}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-destructive">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      aria-invalid={errors.password ? 'true' : 'false'}
                      {...register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-destructive">{errors.password.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold gap-2 group"
                  disabled={submitDisabled}
                >
                  {isSubmitting ? (
                    <motion.div
                      className="h-5 w-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                  ) : (
                    <>
                      Login
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </Button>
              </motion.form>
            </CardContent>
          </Card>

          {/* Mobile-only illustration anchored to the bottom of the
              column, matching the reference mock. Hidden on `lg:` where
              the right-hand panel takes over the artwork.
              `relative left-1/2 -translate-x-1/2 w-screen` is the
              viewport-breakout pattern — the inner column has
              `max-w-md` so plain `w-full` only reached 448 px on a
              tablet, leaving visible margins. Pinning to the screen
              width forces the painted brush strokes to actually touch
              the left and right edges of the device regardless of how
              tightly the form is constrained above. `min-h-[35vh]
              max-h-[50vh] object-cover` gives the artwork the height
              presence the reference shows without ever overflowing the
              available viewport. */}
          <div className="lg:hidden mt-6 relative left-1/2 -translate-x-1/2 w-screen pointer-events-none select-none">
            <img
              src="/images/login.png"
              alt=""
              aria-hidden="true"
              className="w-full h-auto min-h-[35vh] max-h-[50vh] object-cover object-center"
            />
          </div>
        </motion.div>
      </div>

      {/* ─── Right side ─ Brand panel (desktop only) ───────────────────
          Hosts the wordmark logo at the top, the cricket-equipment
          illustration in the middle, and the panel headline directly
          underneath. The previous `justify-between` layout combined
          with the illustration's natural height pushed the "Cricket
          Admin Panel" headline below the visible fold on shorter
          viewports — using a single `justify-center` flow with an
          explicit `gap-y` keeps every element on screen regardless of
          viewport height. Hidden below `lg:` — the mobile column above
          carries the same artwork inline. */}
      <div className="hidden lg:flex flex-1 flex-col items-center justify-center bg-primary/5 relative overflow-hidden rounded-tl-lg rounded-bl-lg py-4 px-2 xl:px-4">
        {/* Decorative gradient blobs — soft palette wash behind the
            illustration so the white bat / red ball pop. */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />

        <motion.div
          // Wrapper `w-full` (no max-width) so the hero illustration
          // touches both edges of the brand panel. Per-child max-widths
          // below keep the logo and text block from stretching to that
          // same full width. Negative `mt` on the illustration pulls
          // it tight against the logo so the whole stack fits without
          // scrolling on common laptop heights.
          className="relative z-10 flex flex-col items-center w-full"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Brand wordmark — sized up to dominate the top of the
              panel as the reference mock shows. Steps up further at
              `xl:` so ultrawide monitors get a larger logo without
              eating into the illustration's height budget below. */}
          <img
            src="/images/web_logo.png"
            alt="Future Protea"
            className="block mx-auto h-48 xl:h-64 w-auto pointer-events-none"
          />

          {/* Hero illustration — the supplied `login.png` asset.
              `w-full` + `max-h-[46vh]` + `object-cover` is the
              combination that actually delivers what the user asked
              for: the image fills the panel width edge-to-edge (cover
              ensures the width-fill always wins over the height cap
              instead of letter-boxing like `object-contain` did) AND
              the height cap keeps enough room below for the headline
              + full tagline so neither needs scrolling. The aggressive
              `-mt-16 xl:-mt-20` pulls the illustration UP against the
              logo so the wordmark's natural transparent bottom-padding
              (carried by the asset itself) is collapsed visually — the
              previous `-mt-4` left a big white gap because the asset
              still has unused pixel rows under "LOG EVERY BALL". The
              tighter `max-h-[46vh]` keeps the headline + tagline
              fully readable underneath without scrolling. */}
          <img
            src="/images/login.png"
            alt=""
            aria-hidden="true"
            className="block w-full h-auto max-h-[46vh] object-cover pointer-events-none select-none -mt-16 xl:-mt-20"
          />

          {/* Headline + tagline — `max-w-md` keeps the line length
              comfortable for reading; the parent's `items-center`
              centres it horizontally. `shrink-0` stops Flexbox from
              squashing this block when the illustration above is
              fighting for vertical space, which is what was clipping
              the tagline to "...from one" before. */}
          <motion.div
            className="text-center max-w-md shrink-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h2 className="text-xl xl:text-2xl font-bold">Cricket Admin Panel</h2>
            <p className="text-sm xl:text-base text-muted-foreground mt-1">
              Manage tournaments, teams, and players from one powerful platform.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
